/**
 * Assinar o laudo com o certificado digital do perito, pelo Assinador SERPRO (E164.F2, GUS-113,
 * ADR-0113). Fica no painel de revisão, no lugar do botão único da assinatura interna:
 *
 * - "Antes da assinatura": o que vai ser assinado e com quê;
 * - o estado do programa neste computador, com a instalação passo a passo quando ele não responde;
 * - um botão pelo papel de quem assina (assistente ou responsável técnico) e o andamento;
 * - a assinatura interna como reserva, atrás de "Outras formas de assinar".
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { assinadorNoAr, PAGINA_DO_ASSINADOR, type Assinador } from '@/lib/assinador_serpro';
import type { EstadoDaAssinaturaIcp, PapelNaAssinatura } from '@/lib/assinatura_icp';
import type { ChecklistRevisao } from '@/lib/certificacao';

import {
  assinarComOCertificado,
  mensagemDaFalha,
  rotuloDaEtapa,
  type EtapaDaAssinatura,
} from './assinaturaIcp';

const t = ptBR.assinaturaIcp;

type SituacaoDoAssinador = 'verificando' | 'conectado' | 'ausente';

export interface AssinarComCertificadoProps {
  client: AxiosInstance;
  requisicaoId: string;
  checklist: ChecklistRevisao;
  completo: boolean;
  papel: PapelNaAssinatura;
  cpfCadastrado: boolean;
  onConcluida: (estado: EstadoDaAssinaturaIcp) => void;
  /** A reserva: a assinatura interna registrada (ADR-0113 §7). Sem ela, a reserva não aparece:
   * no exame eletrônico só a assinatura com certificado digital assina (E170.F1, ADR-0118). */
  onInterna?: (() => void) | undefined;
  assinandoInterna: boolean;
  /** Só para teste: o assinador local dublado. */
  conectar?: () => Promise<Assinador>;
  verificar?: () => Promise<boolean>;
}

function useSituacaoDoAssinador(
  verificar: () => Promise<boolean>,
): [SituacaoDoAssinador, () => void] {
  const [situacao, setSituacao] = useState<SituacaoDoAssinador>('verificando');
  const conferir = useCallback(() => {
    setSituacao('verificando');
    void verificar().then((noAr) => setSituacao(noAr ? 'conectado' : 'ausente'));
  }, [verificar]);
  useEffect(conferir, [conferir]);
  return [situacao, conferir];
}

function ComoInstalar({ onVerificar }: { onVerificar: () => void }): JSX.Element {
  return (
    <div data-testid="assinador-ausente" className="mt-sc-2 text-body-sm text-fg">
      <p className="text-danger-700">{t.assinadorAusente}</p>
      <details className="mt-sc-2" data-testid="assinador-como-instalar">
        <summary className="cursor-pointer text-accent-700">{t.comoInstalar}</summary>
        <ol className="mt-sc-2 list-decimal pl-5">
          {t.passosDaInstalacao.map((passo) => (
            <li key={passo}>{passo}</li>
          ))}
        </ol>
        <a
          href={PAGINA_DO_ASSINADOR}
          target="_blank"
          rel="noreferrer"
          className="mt-sc-2 inline-block text-accent-700 underline-offset-4 hover:underline"
        >
          {t.paginaOficial}
        </a>
      </details>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-sc-2"
        data-testid="assinador-verificar"
        onClick={onVerificar}
      >
        {t.verificarDeNovo}
      </Button>
    </div>
  );
}

interface ReservaProps {
  completo: boolean;
  assinandoInterna: boolean;
  onInterna: () => void;
}

function Reserva({ completo, assinandoInterna, onInterna }: ReservaProps): JSX.Element {
  return (
    <details className="mt-sc-4 text-body-sm" data-testid="assinatura-outras-formas">
      <summary className="cursor-pointer text-fg-muted">{t.outrasFormas}</summary>
      <p className="mt-sc-2 text-fg-muted">{t.semCertificadoAviso}</p>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-sc-2"
        data-testid="assinar-laudo"
        disabled={!completo || assinandoInterna}
        onClick={onInterna}
      >
        {assinandoInterna ? ptBR.certificacao.assinando : t.assinarSemCertificado}
      </Button>
    </details>
  );
}

function SituacaoDoPrograma({
  situacao,
  onVerificar,
}: {
  situacao: SituacaoDoAssinador;
  onVerificar: () => void;
}): JSX.Element {
  if (situacao === 'verificando') {
    return <p className="mt-sc-2 text-body-sm text-fg-muted">{t.assinadorVerificando}</p>;
  }
  if (situacao === 'conectado') {
    return (
      <p data-testid="assinador-conectado" className="mt-sc-2 text-body-sm text-success-700">
        {t.assinadorConectado}
      </p>
    );
  }
  return <ComoInstalar onVerificar={onVerificar} />;
}

function Avisos({
  cpfCadastrado,
  etapa,
  falha,
}: {
  cpfCadastrado: boolean;
  etapa: EtapaDaAssinatura | null;
  falha: string | null;
}): JSX.Element {
  return (
    <>
      {!cpfCadastrado ? (
        <p data-testid="assinatura-sem-cpf" className="mt-sc-2 text-body-sm text-danger-700">
          {t.semCpf}
        </p>
      ) : null}
      {etapa !== null ? (
        <p role="status" data-testid="assinatura-etapa" className="mt-sc-2 text-body-sm text-fg">
          {rotuloDaEtapa(etapa)}
        </p>
      ) : null}
      {falha !== null ? (
        <p
          role="alert"
          data-testid="assinatura-falha"
          className="mt-sc-2 border border-danger bg-danger-50 px-sc-3 py-2 text-body-sm text-danger-700"
        >
          {falha}
        </p>
      ) : null}
    </>
  );
}

export function AssinarComCertificado(props: AssinarComCertificadoProps): JSX.Element {
  const [situacao, verificarDeNovo] = useSituacaoDoAssinador(props.verificar ?? assinadorNoAr);
  const [etapa, setEtapa] = useState<EtapaDaAssinatura | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const rotulo =
    props.papel === 'responsavel_tecnico'
      ? t.assinarComoResponsavel
      : t.assinarComoAssistenciaTecnica;

  async function assinar(): Promise<void> {
    setFalha(null);
    try {
      const estado = await assinarComOCertificado({
        client: props.client,
        requisicaoId: props.requisicaoId,
        checklist: props.checklist,
        onEtapa: setEtapa,
        ...(props.conectar !== undefined ? { conectar: props.conectar } : {}),
      });
      props.onConcluida(estado);
    } catch (err) {
      setFalha(mensagemDaFalha(err));
    } finally {
      setEtapa(null);
    }
  }

  const livre = props.completo && props.cpfCadastrado && situacao === 'conectado' && !etapa;
  return (
    <section className="mt-sc-4 border-t border-border pt-sc-3" data-testid="assinatura-icp">
      <h3 className="text-body font-semibold text-fg">{t.antesTitulo}</h3>
      <p className="mt-1 text-body-sm text-fg-muted">{t.antesTexto}</p>
      <SituacaoDoPrograma situacao={situacao} onVerificar={verificarDeNovo} />
      <Avisos cpfCadastrado={props.cpfCadastrado} etapa={etapa} falha={falha} />
      <Button
        type="button"
        data-testid="assinar-com-certificado"
        disabled={!livre}
        onClick={() => void assinar()}
        className="mt-sc-3"
      >
        {rotulo}
      </Button>
      {props.onInterna !== undefined ? (
        <Reserva
          completo={props.completo}
          assinandoInterna={props.assinandoInterna}
          onInterna={props.onInterna}
        />
      ) : null}
    </section>
  );
}
